import { lazy } from 'react';
import {
  AppBrowserRouter,
  AppNavigate,
  AppRoute,
  AppRoutes,
} from '@/navigation';
import { ROUTES } from '@/routes';
import { PrivateRoute } from '@/components/navigation/PrivateRoute';
import { UnauthenticatedRoute } from './UnauthenticatedRoute';
import { ProviderRoute } from './ProviderRoute';
import { MissingItemRoute } from './MissingItemRoute';
import { TestPage } from '@/pages/test/TestPage';
import { isDevelopment } from '@/config';
import { DevDebugPanel } from '@/devtools/DevDebugPanel';

const ShortenerPage = lazy(() => import('@/pages/auth/ShortenerPage'));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const UnauthenticatedPage = lazy(
  () => import('@/pages/auth/UnauthenticatedPage'),
);
const PotentialTerminationPage = lazy(
  () => import('@/pages/auth/PotentialTerminationPage'),
);
const ApplicationTerminatedPage = lazy(
  () => import('@/pages/auth/ApplicationTerminatedPage'),
);
const InvitationLockedPage = lazy(
  () => import('@/pages/auth/InvitationLockedPage'),
);

const DashboardPage = lazy(() => import('@/pages/home/DashboardPage'));
const ApplicationPage = lazy(
  () => import('@/pages/application/ApplicationPage'),
);
const SectionPage = lazy(() => import('@/pages/section/SectionPage'));
const QuestionsPage = lazy(() => import('@/pages/questions/QuestionsPage'));
const SignaturePage = lazy(() => import('@/pages/signature/SignaturePage'));
const AuthorizationPage = lazy(
  () => import('@/pages/authorization/AuthorizationPage'),
);
const ReviewAndSubmitPage = lazy(
  () => import('@/pages/review-and-submit/ReviewAndSubmitPage'),
);
const ErrorPage = lazy(() => import('@/pages/error/ErrorPage'));
const NoPendingApplicationsPage = lazy(
  () => import('@/pages/auth/NoPendingApplicationsPage'),
);
const MissingItemsPage = lazy(
  () => import('@/pages/missing-items/MissingItemsPage'),
);
const MissingItemsReviewAndSubmitPage = lazy(
  () => import('@/pages/missing-items/MissingItemsReviewAndSubmitPage'),
);
const InstitutionLoginPage = lazy(
  () => import('@/pages/auth/InstitutionLoginPage'),
);

export const NavigationRouter: React.FC = () => {
  return (
    <AppBrowserRouter>
      <DevDebugPanel />
      <AppRoutes>
        <AppRoute element={<PrivateRoute />}>
          <AppRoute path={ROUTES.dashboard} element={<DashboardPage />} />
          <AppRoute path={ROUTES.application} element={<ApplicationPage />} />
          <AppRoute path={ROUTES.section} element={<SectionPage />} />
          <AppRoute path={ROUTES.questions} element={<QuestionsPage />} />

          <AppRoute element={<MissingItemRoute />}>
            <AppRoute path={ROUTES.missingItems} element={<MissingItemsPage />} />
            <AppRoute
              path={ROUTES.missingItemsReviewAndSubmit}
              element={<MissingItemsReviewAndSubmitPage />}
            />
          </AppRoute>

          <AppRoute element={<ProviderRoute />}>
            <AppRoute path={ROUTES.signature} element={<SignaturePage />} />
            <AppRoute
              path={ROUTES.authorization}
              element={<AuthorizationPage />}
            />
            <AppRoute
              path={ROUTES.reviewAndSubmit}
              element={<ReviewAndSubmitPage />}
            />
          </AppRoute>
          <AppRoute
            path={ROUTES.noPendingApplications}
            element={<NoPendingApplicationsPage />}
          />
        </AppRoute>

        <AppRoute element={<UnauthenticatedRoute />}>
          <AppRoute path={ROUTES.shortener} element={<ShortenerPage />} />
          <AppRoute
            path={ROUTES.potentialTermination}
            element={<PotentialTerminationPage />}
          />
          <AppRoute path={ROUTES.login} element={<LoginPage />} />
          <AppRoute
            path={ROUTES.institutionLogin}
            element={<InstitutionLoginPage />}
          />
          <AppRoute
            path={ROUTES.unauthenticated}
            element={<UnauthenticatedPage />}
          />
          <AppRoute
            path={ROUTES.applicationTerminated}
            element={<ApplicationTerminatedPage />}
          />
          <AppRoute
            path={ROUTES.invitationLocked}
            element={<InvitationLockedPage />}
          />
        </AppRoute>

        {isDevelopment() && (
          <>
            <AppRoute path={ROUTES.error} element={<ErrorPage />} />
            <AppRoute element={<TestPage />} path={ROUTES.test} />
          </>
        )}

        <AppRoute
          path={ROUTES.home}
          element={<AppNavigate to={ROUTES.dashboard} />}
        />
        <AppRoute path="*" element={<AppNavigate to={ROUTES.dashboard} />} />
      </AppRoutes>
    </AppBrowserRouter>
  );
};
